import { Link, useParams } from "react-router-dom";
import { Calendar, Clock, ArrowLeft } from "lucide-react";
import { Section, FadeIn } from "../components/Primitives";
import { BLOG_POSTS } from "../data/content";

const fullText = `Indian businesses run on registers — and most of them are still on paper.
Across schools, hospitals, kirana stores, hospitals and government offices, billions of rows of data are still being written by hand every single day. That data is hard to search, easy to lose, and impossible to share at the speed a modern business needs.

This isn't an article about replacing humans with software. It's about replacing the friction of paper with the elasticity of digital. Once a register lives in the cloud, three things change overnight: speed of retrieval, quality of insight, and resilience to disasters (floods, fires, fading ink — pick your favourite). The shift doesn't require an IT team or a Silicon-Valley budget. It requires a smartphone camera and an OCR engine that actually understands the way India writes.

We built Register to Digital exactly for that moment. Capture, structure, search, share — and get back to running the actual business.`;

export default function BlogPostPage() {
  const { slug } = useParams();
  const post = BLOG_POSTS.find((p) => p.slug === slug);
  if (!post) {
    return (
      <Section className="pt-24" testId="blog-post-not-found">
        <h1 className="font-poppins text-3xl font-bold">Post not found</h1>
        <Link to="/blog" className="mt-4 inline-flex items-center gap-1 text-sm font-medium underline">
          <ArrowLeft className="h-4 w-4" /> Back to blog
        </Link>
      </Section>
    );
  }
  return (
    <div data-testid={`page-blog-post-${post.slug}`}>
      <Section className="pt-24 pb-10">
        <FadeIn>
          <Link to="/blog" data-testid="blog-post-back" className="inline-flex items-center gap-1 text-xs uppercase tracking-wider text-zinc-500 hover:text-zinc-950">
            <ArrowLeft className="h-3.5 w-3.5" /> The Register Journal
          </Link>
        </FadeIn>
        <FadeIn delay={0.05}>
          <h1 className="mt-6 max-w-4xl font-poppins text-4xl sm:text-5xl font-bold tracking-tighter text-zinc-950">{post.title}</h1>
        </FadeIn>
        <FadeIn delay={0.1}>
          <p className="mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-wider text-zinc-500">
            <Calendar className="h-3.5 w-3.5" /> {post.date} · <Clock className="h-3.5 w-3.5" /> {post.read}
          </p>
        </FadeIn>
      </Section>

      <Section className="pt-0">
        <div
          className="h-80 w-full rounded-3xl bg-zinc-100"
          style={{ backgroundImage: `url(${post.cover})`, backgroundSize: "cover", backgroundPosition: "center" }}
        />
        <div className="mx-auto mt-10 max-w-3xl">
          <p className="text-lg leading-relaxed text-zinc-700">{post.excerpt}</p>
          {fullText.split("\n\n").map((para, i) => (
            <p key={i} className="mt-6 text-base leading-relaxed text-zinc-700">{para}</p>
          ))}
        </div>
      </Section>
    </div>
  );
}
